import Phaser from 'phaser'

import TextureKeys from '../consts/TextureKeys'
import SceneKeys from '../consts/SceneKeys'
import RocketMouse from '../game/RocketMouse'
import Drone from '../game/Drone'
import AnimationKeys from '../consts/AnimationKeys'
import Bomb from '../game/Bomb'
import BritishFlagProjectile from '../game/BritishFlagProjectile'

export default class Game extends Phaser.Scene {
  private background!: Phaser.GameObjects.TileSprite

  private drones!: Phaser.Physics.Arcade.StaticGroup
  private coins!: Phaser.Physics.Arcade.StaticGroup

  private flags!: Phaser.Physics.Arcade.StaticGroup
  private lastFlagSpawnTime = 0
  private flagSpawnInterval = 7500 // 7.5 seconds in milliseconds (increased frequency)
  private availableFlagTextures: string[] = [
    TextureKeys.PakistanFlag,
    TextureKeys.AfganiFlag,
    TextureKeys.AlbaniaFlag,
    TextureKeys.RussiaFlag,
    TextureKeys.GayFlag,
  ] // Array of flag textures for random selection

  private bombs!: Phaser.Physics.Arcade.Group
  private britishFlags!: Phaser.Physics.Arcade.Group
  private convertedFlags: Phaser.GameObjects.Image[] = [] // Track converted British flags (decorative only)

  private scoreLabel!: Phaser.GameObjects.Text
  private timeLabel!: Phaser.GameObjects.Text
  private instructionLabel!: Phaser.GameObjects.Text
  private score = 0

  private mouse!: RocketMouse
  private backgroundScrollOffset = 0
  private totalScrollDistance = 0 // Track total distance scrolled for background
  private lastScrollX = 0 // Track last camera position to calculate delta
  private gameStartTime = 0
  private pausedTime = 0 // Track time spent paused
  private pauseStartTime = 0 // When the game was paused
  private baseSpeed = 200
  private speedIncreaseRate = 3.33 // Speed increases by ~3.33 units per second (3x slower than before)
  private deathCause: 'flag' | 'drone' | null = null
  private cameraScrollSpeed = 0 // Track camera scroll speed for smooth background sync

  // Zone-based obstacle management
  private readonly ZONE_WIDTH = 500 // Each zone is 500 units wide (tripled drone frequency)
  private occupiedZones: Map<number, 'drone' | 'flag'> = new Map()

  // Mobile controls
  private isMobile = false
  private throwFlagButton?: Phaser.GameObjects.Graphics
  private throwFlagButtonIcon?: Phaser.GameObjects.Text
  private isJetpackPressed = false

  // Drone spawn counter for alternating movement types
  private droneSpawnCounter = 0

  // Sound throttling to prevent audio glitching when collecting many coins at once
  private coinSound?: Phaser.Sound.BaseSound
  private backgroundMusic?: Phaser.Sound.BaseSound
  private flagSound?: Phaser.Sound.BaseSound
  private deathSound?: Phaser.Sound.BaseSound
  private explosionSound?: Phaser.Sound.BaseSound
  private coinSoundCounter = 0
  private lastCoinSoundTime = 0
  private readonly COIN_SOUND_PLAY_EVERY = 15 // Only play sound every 15th coin (more aggressive)
  private readonly COIN_SOUND_MIN_DELAY = 200 // Minimum 200ms between sounds (longer delay)

  // Flag reload system
  private flagsRemaining = 10
  private readonly MAX_FLAGS = 10
  private isReloading = false
  private reloadStartTime = 0
  private readonly RELOAD_DURATION = 5000 // 5 seconds to reload
  private reloadBar?: Phaser.GameObjects.Graphics
  private reloadText?: Phaser.GameObjects.Text
  private flagCountText?: Phaser.GameObjects.Text

  constructor() {
    super(SceneKeys.Game)
  }

  shutdown() {
    // Clean up sounds when scene shuts down (game over)
    // Stop and remove all sounds to prevent multiple instances
    if (this.backgroundMusic) {
      this.backgroundMusic.stop()
      this.backgroundMusic.destroy()
      this.backgroundMusic = undefined
    }
    if (this.coinSound) {
      this.coinSound.stop()
      this.coinSound.destroy()
      this.coinSound = undefined
    }
    if (this.flagSound) {
      this.flagSound.stop()
      this.flagSound.destroy()
      this.flagSound = undefined
    }
    if (this.deathSound) {
      this.deathSound.stop()
      this.deathSound.destroy()
      this.deathSound = undefined
    }
    if (this.explosionSound) {
      this.explosionSound.stop()
      this.explosionSound.destroy()
      this.explosionSound = undefined
    }

    // Clean up event listeners
    this.game.events.off('pause', this.handlePause, this)
    this.game.events.off('resume', this.handleResume, this)
    this.game.events.off('hidden', this.handlePause, this)
    this.game.events.off('visible', this.handleResume, this)
  }

  init() {
    this.score = 0
    this.backgroundScrollOffset = 0
    this.totalScrollDistance = 0
    this.lastScrollX = 0
    this.gameStartTime = 0
    this.pausedTime = 0
    this.pauseStartTime = 0
    this.lastFlagSpawnTime = 0
    this.deathCause = null
    this.occupiedZones = new Map()
    this.convertedFlags = []
    this.droneSpawnCounter = 0 // Reset drone counter
    this.coinSoundCounter = 0 // Reset sound counter
    this.lastCoinSoundTime = 0 // Reset sound timer
    this.flagsRemaining = this.MAX_FLAGS // Reset flags to 10
    this.isReloading = false // Reset reload state
    this.reloadStartTime = 0
  }

  create() {
    const width = this.scale.width
    const height = this.scale.height

    // Detect if mobile/touch device - use input capabilities instead of OS detection
    this.isMobile = this.sys.game.device.input.touch || !this.sys.game.device.os.desktop

    // Background image is 1648x720, which matches our canvas size perfectly
    // Scale factor: 720 / 720 = 1.0 (no scaling needed!)
    const bgScale = height / 720

    this.background = this.add.tileSprite(0, 0, width, height, TextureKeys.Background)
    this.background.setOrigin(0, 0)
    this.background.setScrollFactor(0) // Keep at 0 so we can manually control scrolling
    this.background.setTileScale(bgScale, bgScale)

    // Create drones group (static)
    this.drones = this.physics.add.staticGroup({
      classType: Drone,
      runChildUpdate: true, // Enable preUpdate for animations
    })

    this.coins = this.physics.add.staticGroup()

    // Spell "BRIT CARD" with coins at the beginning
    this.spawnBritCardCoins()

    this.spawnCoins()

    this.flags = this.physics.add.staticGroup()

    // Don't create any flags initially - they'll spawn based on timer

    this.bombs = this.physics.add.group({
      classType: Bomb,
      maxSize: 10,
      runChildUpdate: true,
    })

    this.britishFlags = this.physics.add.group({
      classType: BritishFlagProjectile,
      maxSize: 50, // Increased from 10 to 50 for more flags
      runChildUpdate: true,
    })

    this.mouse = new RocketMouse(this, width * 0.25, height - 30)
    this.add.existing(this.mouse)

    const body = this.mouse.body as Phaser.Physics.Arcade.Body
    body.setCollideWorldBounds(true)
    body.setVelocityX(this.baseSpeed)

    this.gameStartTime = this.time.now
    this.cameraScrollSpeed = this.baseSpeed

    // Configure sound manager to prevent audio glitching
    this.sound.pauseOnBlur = false

    // Handle visibility change to prevent speed acceleration while tab is inactive
    this.game.events.on('pause', this.handlePause, this)
    this.game.events.on('resume', this.handleResume, this)
    this.game.events.on('hidden', this.handlePause, this)
    this.game.events.on('visible', this.handleResume, this)

    // Create and play background music FIRST (higher priority)
    // Check if music is already playing globally to prevent multiple instances
    const existingMusic = this.sound.get('background-music')
    if (existingMusic && existingMusic.isPlaying) {
      // Music already playing, just reference it
      this.backgroundMusic = existingMusic
    } else {
      // Stop any existing music first
      this.sound.stopByKey('background-music')

      // Create new music instance
      this.backgroundMusic = this.sound.add('background-music', {
        volume: 0.5,
        loop: true,
      })
      this.backgroundMusic.play()
    }

    // Create a single reusable coin sound instance to prevent audio overload
    // Lower volume to not interfere with music
    this.coinSound = this.sound.add('coin-sound', {
      volume: 0.2,
      loop: false,
    })

    // Create flag throw sound
    this.flagSound = this.sound.add('flag-throw', {
      volume: 0.7,
      loop: false,
    })

    // Create death sound
    this.deathSound = this.sound.add('death-sound', {
      volume: 0.7,
      loop: false,
    })

    // Create explosion sound
    this.explosionSound = this.sound.add('explosion-sound', {
      volume: 0.35,
      loop: false,
    })

    this.scoreLabel = this.add
      .text(10, 10, `Score: ${this.score}`, {
        fontSize: '24px',
        color: '#080808',
        backgroundColor: '#F8E71C',
        shadow: { fill: true, blur: 0, offsetY: 0 },
        padding: { left: 15, right: 15, top: 10, bottom: 10 },
      })
      .setScrollFactor(0)

    // Add time display
    this.timeLabel = this.add
      .text(10, 50, `Time: 0:00`, {
        fontSize: '24px',
        color: '#080808',
        backgroundColor: '#F8E71C',
        shadow: { fill: true, blur: 0, offsetY: 0 },
        padding: { left: 15, right: 15, top: 10, bottom: 10 },
      })
      .setScrollFactor(0)

    // Add flag count display
    this.flagCountText = this.add
      .text(10, 90, `Flags: ${this.flagsRemaining}/${this.MAX_FLAGS}`, {
        fontSize: '24px',
        color: '#080808',
        backgroundColor: '#F8E71C',
        shadow: { fill: true, blur: 0, offsetY: 0 },
        padding: { left: 15, right: 15, top: 10, bottom: 10 },
      })
      .setScrollFactor(0)

    // Add instruction text (hide on mobile)
    if (!this.isMobile) {
      this.instructionLabel = this.add
        .text(width - 10, 10, `Press D to throw flag`, {
          fontSize: '20px',
          color: '#080808',
          backgroundColor: '#F8E71C',
          shadow: { fill: true, blur: 0, offsetY: 0 },
          padding: { left: 15, right: 15, top: 10, bottom: 10 },
        })
        .setOrigin(1, 0)
        .setScrollFactor(0)
    }

    // ALWAYS create touch controls - they work alongside keyboard controls
    this.createTouchControls(width, height)

    this.physics.world.setBounds(0, 0, Number.MAX_SAFE_INTEGER, height - 60)

    // Disable camera lerp (smoothing) to prevent jitter - camera follows instantly
    this.cameras.main.startFollow(this.mouse, false)
    this.cameras.main.setFollowOffset(-width * 0.25, 0)
    this.cameras.main.setBounds(0, 0, Number.MAX_SAFE_INTEGER, height)

    // Enable pixel rounding to prevent sub-pixel rendering artifacts
    this.cameras.main.roundPixels = true

    this.physics.add.overlap(this.drones, this.mouse, this.handleOverlapDrone, undefined, this)
    this.physics.add.overlap(this.coins, this.mouse, this.handleCollectCoin, undefined, this)
    this.physics.add.overlap(this.flags, this.mouse, this.handleOverlapFlag, undefined, this)
    this.physics.add.overlap(this.bombs, this.flags, this.handleBombHitFlag, undefined, this)
    this.physics.add.overlap(this.britishFlags, this.flags, this.handleBritishFlagHitEnemyFlag, undefined, this)
    this.physics.add.overlap(this.britishFlags, this.drones, this.handleBritishFlagHitDrone, undefined, this)

    // Create coin spin animation from sprite sheet
    this.anims.create({
      key: AnimationKeys.CoinSpin,
      frames: this.anims.generateFrameNames(TextureKeys.CoinSheet, {
        prefix: 'gold_coin_round_star_',
        suffix: '.png',
        start: 1,
        end: 6,
        zeroPad: 0,
      }),
      frameRate: 10,
      repeat: -1,
    })

    // Create drone hover animation from sprite sheet
    this.anims.create({
      key: AnimationKeys.DroneHover,
      frames: this.anims.generateFrameNames(TextureKeys.DroneSheet, {
        prefix: 'drone_frame_',
        suffix: '.png',
        start: 0,
        end: 8,
        zeroPad: 0,
      }),
      frameRate: 4, // Slowed down by 300% (12 / 3 = 4)
      repeat: -1,
    })

    // Spawn initial drone after BRIT CARD message area (after animations are created)
    this.spawnDrone(2700)
  }

  // Universal method to play coin sound with throttling
  private playCoinSound() {
    // AGGRESSIVE sound throttling - use BOTH counter AND time to prevent audio glitching
    this.coinSoundCounter++
    const currentTime = this.time.now
    const timeSinceLastSound = currentTime - this.lastCoinSoundTime

    // Only play sound if ALL conditions are met:
    // 1. We've collected enough coins (every 15th)
    // 2. Enough time has passed (at least 200ms)
    // 3. The sound isn't currently playing
    // 4. Background music is playing (don't interrupt music loading)
    if (
      this.coinSoundCounter >= this.COIN_SOUND_PLAY_EVERY &&
      timeSinceLastSound >= this.COIN_SOUND_MIN_DELAY &&
      this.coinSound &&
      !this.coinSound.isPlaying &&
      this.backgroundMusic &&
      this.backgroundMusic.isPlaying
    ) {
      // Use the single reusable sound instance instead of creating new ones
      // Stop first to ensure clean playback
      this.coinSound.stop()
      this.coinSound.play()
      this.coinSoundCounter = 0
      this.lastCoinSoundTime = currentTime
    }
  }

  private handleCollectCoin(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const coin = obj2 as Phaser.Physics.Arcade.Sprite

    // Play coin sound with throttling
    this.playCoinSound()

    // Stop any tweens on this coin
    this.tweens.killTweensOf(coin)

    // Get coin position before hiding it
    const coinX = coin.x
    const coinY = coin.y
    const coinFrame = coin.frame.name

    // Immediately hide and disable the coin
    this.coins.killAndHide(coin)
    ;(coin.body as Phaser.Physics.Arcade.StaticBody).enable = false

    // Create a temporary visual coin for the animation
    this.animateCoinToScore(coinX, coinY, coinFrame)

    this.score += 10
    this.scoreLabel.text = `Score: ${this.score}`
  }

  private animateCoinToScore(worldX: number, worldY: number, frameName: string) {
    // Convert world position to screen position
    const screenX = worldX - this.cameras.main.scrollX
    const screenY = worldY - this.cameras.main.scrollY

    // Create a temporary coin sprite for animation (not in physics group)
    const tempCoin = this.add.sprite(screenX, screenY, TextureKeys.CoinSheet, frameName)
    tempCoin.setScrollFactor(0) // Stay in screen space
    tempCoin.play(AnimationKeys.CoinSpin)

    // Animate to score label
    this.tweens.add({
      targets: tempCoin,
      x: 60,
      y: 35,
      scale: 0.5,
      alpha: 0.8,
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        // Destroy the temporary sprite
        tempCoin.destroy()
      },
    })
  }

  private handleOverlapDrone(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const mouse = obj1 as RocketMouse
    const drone = obj2 as Drone

    // Skip if drone is inactive or invisible (already destroyed)
    if (!drone.active || !drone.visible) {
      return
    }

    // Check if drone's physics body is enabled
    const droneBody = drone.body as Phaser.Physics.Arcade.StaticBody
    if (!droneBody || !droneBody.enable) {
      return
    }

    // Check if player is falling AND above the drone
    if (mouse.body && mouse.body.velocity.y > 0 && mouse.y < drone.y) {
      // Player is stomping the drone
      this.destroyDrone(drone)
      mouse.bounce()
      // No death sound on stomp, only on collision
    } else {
      // Normal collision - player dies
      this.deathCause = 'drone'
      this.deathSound?.play() // Play death sound
      mouse.kill()
    }
  }

  private destroyDrone(drone: Drone) {
    // Play explosion sound
    if (this.explosionSound) {
      this.explosionSound.play()
    }

    // Remove drone from the group and zone tracking
    const droneX = drone.x
    const droneZone = this.getZoneForPosition(droneX)
    if (this.occupiedZones.get(droneZone) === 'drone') {
      this.occupiedZones.delete(droneZone)
    }

    // IMMEDIATELY mark drone as inactive and disable physics body to prevent collisions
    drone.setActive(false)
    drone.setVisible(false)

    // Disable the physics body IMMEDIATELY
    const droneBody = drone.body as Phaser.Physics.Arcade.StaticBody
    droneBody.enable = false

    // Remove drone from static group completely
    this.drones.remove(drone, true, true)

    // Convert drone to a dynamic physics object for the destruction animation
    const droneSprite = this.add.container(drone.x, drone.y)
    const sprite = this.add.sprite(0, 0, TextureKeys.DroneSheet, 'drone_frame_0.png')
    sprite.setScale(0.3)
    droneSprite.add(sprite)

    // Add dynamic physics to the container
    this.physics.add.existing(droneSprite, false)
    const body = droneSprite.body as Phaser.Physics.Arcade.Body

    // Apply random velocity - mostly downward but with random horizontal spin
    const randomXVelocity = Phaser.Math.Between(-300, 300)
    const randomYVelocity = Phaser.Math.Between(100, 400) // Positive = downward
    const randomAngularVelocity = Phaser.Math.Between(-500, 500) // Spinning speed

    body.setVelocity(randomXVelocity, randomYVelocity)
    body.setAngularVelocity(randomAngularVelocity)
    body.setGravityY(500) // Apply gravity
    body.setAllowGravity(true)

    // Destroy after falling off screen
    this.time.delayedCall(3000, () => {
      if (droneSprite && droneSprite.scene) {
        droneSprite.destroy()
      }
    })

    // Award points for stomping a drone
    this.score += 50
    this.scoreLabel.text = `Score: ${this.score}`
  }

  private handleOverlapFlag(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    // Check which object is the mouse
    let mouse: RocketMouse
    if ((obj1 as any).mouseState !== undefined) {
      mouse = obj1 as RocketMouse
    } else {
      mouse = obj2 as RocketMouse
    }

    this.deathCause = 'flag'
    mouse.kill()
  }

  private handleBombHitFlag(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const bomb = obj1 as Bomb
    const flag = obj2 as Phaser.Physics.Arcade.Sprite

    // Destroy both bomb and flag
    bomb.destroy()
    this.flags.killAndHide(flag)
    ;(flag.body as Phaser.Physics.Arcade.StaticBody).enable = false

    // Award bonus points for destroying a flag
    this.score += 10
    this.scoreLabel.text = `Score: ${this.score}`

    // Reset bomb cooldown so player can drop another
    this.mouse.resetBombCooldown()
  }

  private handleBritishFlagHitEnemyFlag(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const britishFlag = obj1 as BritishFlagProjectile
    const enemyFlag = obj2 as Phaser.Physics.Arcade.Sprite

    // Get position of enemy flag
    const flagX = enemyFlag.x

    // Remove enemy flag from zone tracking
    const zone = this.getZoneForPosition(enemyFlag.x)
    if (this.occupiedZones.get(zone) === 'flag') {
      this.occupiedZones.delete(zone)
    }

    // Destroy the enemy flag
    this.flags.killAndHide(enemyFlag)
    ;(enemyFlag.body as Phaser.Physics.Arcade.StaticBody).enable = false

    // Destroy the British flag projectile
    britishFlag.destroy()

    // Randomize vertical position for the British flag within the ground area
    // Ground area spans roughly 140-150 pixels in height
    const minY = 520 // Top of ground area (absolute value)
    const maxY = 680 // Bottom of ground area (absolute value)
    const randomY = Phaser.Math.Between(minY, maxY)

    // Create a new British flag in its place with random Y position (no longer a threat, just decorative)
    const newBritishFlag = this.add.image(flagX, randomY, TextureKeys.BritainFlag)
    newBritishFlag.setOrigin(0.5, 1)
    newBritishFlag.setScale(1, 0.5)

    // Track this converted flag so we can clean it up
    this.convertedFlags.push(newBritishFlag)

    // Award bonus points for converting a flag
    this.score += 100
    this.scoreLabel.text = `Score: ${this.score}`

    // Show "Territory Reclaimed" message at the flag position
    this.showTerritoryReclaimedMessage(flagX, randomY)

    // Reset flag cooldown so player can launch another
    this.mouse.resetFlagCooldown()
  }

  private showTerritoryReclaimedMessage(x: number, y: number) {
    const messageText = this.add
      .text(x, y - 80, 'TERRITORY RECLAIMED', {
        fontSize: '32px',
        color: '#FFD700',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
        shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 4, fill: true },
      })
      .setOrigin(0.5)
      .setAlpha(0)

    // Animate the message appearing and fading
    this.tweens.add({
      targets: messageText,
      alpha: 1,
      y: y - 100,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        // Hold for a moment then fade out
        this.tweens.add({
          targets: messageText,
          alpha: 0,
          y: y - 120,
          duration: 500,
          delay: 800,
          ease: 'Power2',
          onComplete: () => {
            messageText.destroy()
          },
        })
      },
    })
  }

  private handleBritishFlagHitCoin(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const britishFlag = obj1 as BritishFlagProjectile
    const coin = obj2 as Phaser.Physics.Arcade.Sprite

    // Play coin sound with universal throttling method
    this.playCoinSound()

    // Stop any tweens on this coin
    this.tweens.killTweensOf(coin)

    // Get coin position before hiding it
    const coinX = coin.x
    const coinY = coin.y
    const coinFrame = coin.frame.name

    // Immediately hide and disable the coin
    this.coins.killAndHide(coin)
    ;(coin.body as Phaser.Physics.Arcade.StaticBody).enable = false

    // Create a temporary visual coin for the animation
    this.animateCoinToScore(coinX, coinY, coinFrame)

    // Award points
    ++this.score
    this.scoreLabel.text = `Score: ${this.score}`

    // Note: We don't destroy the British flag - it continues flying to hit more coins!
  }

  private handleBritishFlagHitDrone(
    obj1: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody,
    obj2: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile | Phaser.Types.Physics.Arcade.GameObjectWithBody
  ) {
    const britishFlag = obj1 as BritishFlagProjectile
    const drone = obj2 as Drone

    // Check if flag and drone are both active to prevent double-hits
    if (!britishFlag.active || !drone.active || !drone.visible) {
      return
    }

    // CRITICAL: Check if drone's physics body is enabled
    const droneBody = drone.body as Phaser.Physics.Arcade.StaticBody
    if (!droneBody || !droneBody.enable) {
      console.warn('Skipping hit on disabled drone body')
      return
    }

    console.log(`DRONE HIT! Removing drone at x=${drone.x}`)

    // Make the flag drop down instead of continuing
    const flagBody = britishFlag.body as Phaser.Physics.Arcade.Body
    flagBody.setVelocityX(0) // Stop horizontal movement
    flagBody.setVelocityY(400) // Fast downward velocity
    flagBody.setAccelerationY(0) // Remove any acceleration

    // The flag will be cleaned up by cleanupBritishFlags when it lands
    // Don't kill it here - let it fall naturally

    // Reset cooldown so player can throw another immediately
    this.mouse.resetFlagCooldown()

    // Destroy the drone using the shared method
    this.destroyDrone(drone)
  }

  update(t: number, dt: number) {
    // Calculate actual elapsed time, excluding paused time
    const actualElapsedMs = t - this.gameStartTime - this.pausedTime
    const elapsedSeconds = Math.floor(actualElapsedMs / 1000)
    const minutes = Math.floor(elapsedSeconds / 60)
    const seconds = elapsedSeconds % 60
    const secondsStr = seconds < 10 ? '0' + seconds : seconds.toString()
    this.timeLabel.text = `Time: ${minutes}:${secondsStr}`

    // Only update speed if the mouse is still alive
    if (!this.mouse.isDead()) {
      // Continuously increase speed over time (no max limit - infinite acceleration!)
      const elapsedSecondsFloat = actualElapsedMs / 1000
      const currentSpeed = this.baseSpeed + elapsedSecondsFloat * this.speedIncreaseRate

      const body = this.mouse.body as Phaser.Physics.Arcade.Body
      body.setVelocityX(currentSpeed)

      // Handle flag reload system
      if (this.isReloading) {
        // Update reload progress automatically
        this.updateReload(t)
      }

      // Handle bomb dropping
      if (this.mouse.isBombKeyPressed()) {
        this.dropBomb()
      }

      // Handle British flag launching (only if not reloading)
      if (!this.isReloading && (this.mouse.isFlagKeyPressed() || (this.isMobile && this.isThrowFlagPressed()))) {
        this.launchBritishFlag()
      }

      // Update RocketMouse with external jetpack state (from touch button)
      this.mouse.setExternalJetpack(this.isJetpackPressed)
    }

    // Calculate how much the camera has moved since last frame
    const currentScrollX = this.cameras.main.scrollX
    const scrollDelta = currentScrollX - this.lastScrollX

    // Add to total scroll distance (this accumulates even through teleports)
    this.totalScrollDistance += scrollDelta
    this.lastScrollX = currentScrollX

    // Update background based on total distance traveled
    this.background.tilePositionX = this.totalScrollDistance

    this.wrapDrones()
    this.wrapFlags()
    this.cleanupCoins()
    this.cleanupBombs()
    this.cleanupBritishFlags()
    this.cleanupConvertedFlags()

    this.teleportBackwards()
  }

  private dropBomb() {
    const bomb = this.bombs.get(this.mouse.x, this.mouse.y) as Bomb
    if (bomb) {
      bomb.setActive(true)
      bomb.setVisible(true)
      this.mouse.setBombDropped()
    }
  }

  private launchBritishFlag() {
    // Check if we have flags remaining
    if (this.flagsRemaining <= 0) {
      // No flags left, start reload
      if (!this.isReloading) {
        this.startReload()
      }
      return
    }

    // Play throw animation
    this.mouse.playThrowAnimation()

    // Play flag throw sound
    if (this.flagSound) {
      this.flagSound.play()
    }

    const flag = this.britishFlags.get(this.mouse.x + 50, this.mouse.y - 30) as BritishFlagProjectile
    if (flag) {
      flag.setActive(true)
      flag.setVisible(true)

      // Calculate the player's current speed
      const mouseBody = this.mouse.body as Phaser.Physics.Arcade.Body
      const playerSpeed = mouseBody.velocity.x

      // Set flag velocity to be player speed + additional boost (always faster than player)
      // Add 600 extra speed to ensure it always moves ahead
      const flagHorizontalSpeed = playerSpeed + 600

      // Set the flag physics with gravity enabled for a realistic arc
      const body = flag.body as Phaser.Physics.Arcade.Body
      body.setAllowGravity(true) // Enable gravity so it arcs downward
      body.setVelocity(flagHorizontalSpeed, -200) // Fly right with scaled speed and up (-200) for an arc

      this.mouse.setFlagLaunched()

      // Decrease flag count
      this.flagsRemaining--
      this.updateFlagCountDisplay()
    }
  }

  private updateFlagCountDisplay() {
    if (this.flagCountText) {
      this.flagCountText.setText(`Flags: ${this.flagsRemaining}/${this.MAX_FLAGS}`)

      // Change color based on remaining flags
      if (this.flagsRemaining === 0) {
        this.flagCountText.setBackgroundColor('#FF0000')
      } else if (this.flagsRemaining <= 3) {
        this.flagCountText.setBackgroundColor('#FFA500')
      } else {
        this.flagCountText.setBackgroundColor('#F8E71C')
      }
    }
  }

  private cleanupBombs() {
    const scrollX = this.cameras.main.scrollX
    this.bombs.children.each((child) => {
      const bomb = child as Bomb
      if (bomb.active && (bomb.x < scrollX - 100 || bomb.y > this.scale.height)) {
        this.bombs.killAndHide(bomb)
      }
      return true
    })
  }

  private cleanupBritishFlags() {
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width
    const groundLevel = this.scale.height - 60 // Same as world bounds

    this.britishFlags.children.each((child) => {
      const flag = child as BritishFlagProjectile
      if (!flag.active) {
        return true
      }

      const body = flag.body as Phaser.Physics.Arcade.Body

      // Check if flag has landed on the ground
      if (flag.y >= groundLevel && body.velocity.y >= 0) {
        // Plant the flag into the ground
        this.plantBritishFlag(flag.x, groundLevel)

        // Remove the projectile
        this.britishFlags.killAndHide(flag)

        // Reset cooldown so player can throw another
        this.mouse.resetFlagCooldown()
      }
      // Check if flag went off screen
      else if (flag.x > rightEdge + 200 || flag.y > this.scale.height + 100 || flag.y < -100 || flag.x < scrollX - 200) {
        this.britishFlags.killAndHide(flag)
        // Reset cooldown when flag goes off screen
        this.mouse.resetFlagCooldown()
      }

      return true
    })
  }

  private plantBritishFlag(x: number, y: number) {
    // Create a planted British flag (decorative, no collision)
    const plantedFlag = this.add.image(x, y, TextureKeys.BritainFlag)
    plantedFlag.setOrigin(0.5, 1)
    plantedFlag.setScale(1, 0.5)

    // Track this planted flag so we can clean it up when it scrolls off screen
    this.convertedFlags.push(plantedFlag)
  }

  private cleanupConvertedFlags() {
    // Don't clean up planted flags - they should stay permanently in the world
    // They will teleport back with the world in teleportBackwards()
  }

  private teleportBackwards() {
    const scrollX = this.cameras.main.scrollX
    const maxX = 2380

    if (scrollX > maxX) {
      this.mouse.x -= maxX

      // Update flags and their zones
      this.flags.children.each((child) => {
        const flag = child as Phaser.Physics.Arcade.Sprite
        if (flag.active) {
          // Clear old zone
          const oldZone = this.getZoneForPosition(flag.x)
          if (this.occupiedZones.get(oldZone) === 'flag') {
            this.occupiedZones.delete(oldZone)
          }

          // Move flag
          flag.x -= maxX

          // Register new zone
          const newZone = this.getZoneForPosition(flag.x)
          this.occupiedZones.set(newZone, 'flag')

          if (flag.body) {
            const body = flag.body as Phaser.Physics.Arcade.StaticBody
            body.updateFromGameObject()
          }
        }
        return true
      })

      // Update drones and their zones
      this.drones.children.each((child) => {
        const drone = child as Drone

        // Clean up inactive drones and their zone occupation
        if (!drone.active) {
          const oldZone = this.getZoneForPosition(drone.x)
          if (this.occupiedZones.get(oldZone) === 'drone') {
            this.occupiedZones.delete(oldZone)
          }
          // Remove inactive drone from group
          this.drones.remove(drone, true, true)
          return true
        }

        // Clear old zone
        const oldDroneZone = this.getZoneForPosition(drone.x)
        if (this.occupiedZones.get(oldDroneZone) === 'drone') {
          this.occupiedZones.delete(oldDroneZone)
        }

        // Teleport drone position
        drone.x -= maxX

        // CRITICAL: Update initial position for movement patterns
        drone.updateInitialPosition(drone.x, drone.y)

        // Update physics body
        const droneBody = drone.body as Phaser.Physics.Arcade.StaticBody
        if (droneBody) {
          droneBody.position.x = drone.x + droneBody.offset.x
          droneBody.position.y = drone.y + droneBody.offset.y
        }

        // Register new zone
        const newDroneZone = this.getZoneForPosition(drone.x)
        this.occupiedZones.set(newDroneZone, 'drone')

        console.log(`Teleported drone back from x=${drone.x + maxX} to x=${drone.x}`)

        return true
      })

      // Spawn new coins at the right edge
      this.spawnCoins()

      // Teleport existing coins back
      this.coins.children.each((child) => {
        const coin = child as Phaser.Physics.Arcade.Sprite
        if (!coin.active) {
          return true
        }

        coin.x -= maxX
        const body = coin.body as Phaser.Physics.Arcade.StaticBody
        body.updateFromGameObject()

        return true
      })

      // Teleport planted British flags back
      this.convertedFlags.forEach((flag) => {
        flag.x -= maxX
      })

      // Update lastScrollX to account for the teleport
      // This prevents a huge delta in the next frame
      this.lastScrollX -= maxX
    }
  }

  private spawnBritCardCoins() {
    // Define pixel-style letter patterns (5x7 grid, 1 = coin, 0 = empty)
    const letterPatterns: { [key: string]: number[][] } = {
      B: [
        [1, 1, 1, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 0],
      ],
      R: [
        [1, 1, 1, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 0],
        [1, 0, 1, 0, 0],
        [1, 0, 0, 1, 0],
        [1, 0, 0, 0, 1],
      ],
      I: [
        [1, 1, 1, 1, 1],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [1, 1, 1, 1, 1],
      ],
      T: [
        [1, 1, 1, 1, 1],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
      ],
      C: [
        [0, 1, 1, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 0],
        [1, 0, 0, 0, 0],
        [1, 0, 0, 0, 0],
        [1, 0, 0, 0, 1],
        [0, 1, 1, 1, 0],
      ],
      A: [
        [0, 0, 1, 0, 0],
        [0, 1, 0, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
      ],
      D: [
        [1, 1, 1, 1, 0],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 0],
      ],
    }

    const coinSize = 40 // Spacing between coins
    const startX = 800 // Start position in world (after initial spawn)
    const startY = 250 // Vertical center position

    const text = 'BRIT CARD'
    let currentX = startX

    for (let i = 0; i < text.length; i++) {
      const char = text[i]

      if (char === ' ') {
        // Add space between words
        currentX += coinSize * 3
        continue
      }

      const pattern = letterPatterns[char]
      if (!pattern) continue

      // Draw the letter with coins
      for (let row = 0; row < pattern.length; row++) {
        for (let col = 0; col < pattern[row].length; col++) {
          if (pattern[row][col] === 1) {
            const coinX = currentX + col * coinSize
            const coinY = startY + row * coinSize

            const coin = this.coins.get(coinX, coinY, TextureKeys.CoinSheet, 'gold_coin_round_star_1.png') as Phaser.Physics.Arcade.Sprite
            coin.setVisible(true)
            coin.setActive(true)
            coin.play(AnimationKeys.CoinSpin)

            const body = coin.body as Phaser.Physics.Arcade.StaticBody
            body.setCircle(body.width * 0.5)
            body.enable = true
            body.updateFromGameObject()

            // Add subtle bouncing animation with stagger
            this.tweens.add({
              targets: coin,
              y: coinY - 8,
              duration: 600,
              ease: 'Sine.easeInOut',
              yoyo: true,
              repeat: -1,
              delay: (row * 5 + col * 5) * 10,
              onUpdate: () => {
                body.updateFromGameObject()
              },
            })
          }
        }
      }

      // Move to next letter position
      currentX += pattern[0].length * coinSize + coinSize * 2 // Add letter spacing
    }
  }

  private spawnCoins() {
    // Don't kill existing coins - just spawn new ones at the right edge
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    // Don't spawn coins in the BRIT CARD area (X: 700-2600)
    if (rightEdge < 2600) {
      return
    }

    let x = rightEdge + 100

    const numCoins = Phaser.Math.Between(1, 20)

    for (let i = 0; i < numCoins; ++i) {
      const coinY = Phaser.Math.Between(110, this.scale.height - 110)
      const coin = this.coins.get(x, coinY, TextureKeys.CoinSheet, 'gold_coin_round_star_1.png') as Phaser.Physics.Arcade.Sprite
      coin.setVisible(true)
      coin.setActive(true)
      coin.play(AnimationKeys.CoinSpin)

      const body = coin.body as Phaser.Physics.Arcade.StaticBody
      body.setCircle(body.width * 0.5)
      body.enable = true

      body.updateFromGameObject()

      // Kill any existing tweens on this coin to prevent accumulation
      this.tweens.killTweensOf(coin)

      // Add bouncing animation
      this.tweens.add({
        targets: coin,
        y: coinY - 10,
        duration: 500,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: i * 50, // Stagger the bounce for a wave effect
        onUpdate: () => {
          // Update the physics body position to follow the coin's animation
          body.updateFromGameObject()
        },
      })

      x += coin.width * 1.5
    }
  }

  private cleanupCoins() {
    // Remove coins that have scrolled off the left side of the screen
    const scrollX = this.cameras.main.scrollX
    this.coins.children.each((child) => {
      const coin = child as Phaser.Physics.Arcade.Sprite
      if (coin.active && coin.x < scrollX - 100) {
        // Kill tweens before hiding the coin
        this.tweens.killTweensOf(coin)
        this.coins.killAndHide(coin)
        ;(coin.body as Phaser.Physics.Arcade.StaticBody).enable = false
      }
      return true
    })
  }

  private getZoneForPosition(x: number): number {
    return Math.floor(x / this.ZONE_WIDTH)
  }

  private findAvailableZone(startZone: number): number {
    // Look for an empty zone, starting from startZone
    // Check at least 2 zones ahead to ensure minimum separation
    for (let i = 2; i < 15; i++) {
      const checkZone = startZone + i
      if (!this.occupiedZones.has(checkZone)) {
        return checkZone
      }
    }
    // If no empty zone found, use the furthest one
    return startZone + 15
  }

  private spawnDrone(xPosition: number) {
    const height = this.scale.height

    // Increment counter
    this.droneSpawnCounter++

    // Assign different movement types based on counter
    let movementType: 'float' | 'vertical' | 'horizontal' | 'circle'
    const typeSelector = this.droneSpawnCounter % 4

    if (typeSelector === 1) {
      movementType = 'vertical' // Every 4th drone starting at 1 (1, 5, 9, 13...)
    } else if (typeSelector === 2) {
      movementType = 'horizontal' // Every 4th drone starting at 2 (2, 6, 10, 14...)
    } else if (typeSelector === 3) {
      movementType = 'circle' // Every 4th drone starting at 3 (3, 7, 11, 15...)
    } else {
      movementType = 'float' // Every 4th drone starting at 0 (4, 8, 12, 16...)
    }

    console.log(`Spawning drone #${this.droneSpawnCounter} with movement: ${movementType}`)

    // Randomize vertical position (drones can fly at different heights)
    const minY = 150 // Higher up in the sky
    const maxY = 400 // Lower in the sky but not on ground
    const randomY = Phaser.Math.Between(minY, maxY)

    const drone = new Drone(this, xPosition, randomY, movementType)
    this.drones.add(drone) // Only add to group, not to scene separately

    // Register drone position in zone system
    const droneZone = this.getZoneForPosition(xPosition)
    this.occupiedZones.set(droneZone, 'drone')
  }

  private wrapDrones() {
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    // Clean up drones that are far behind the camera
    const cleanupDistance = 1000 // Remove drones 1000px behind camera
    this.drones.children.each((child) => {
      const drone = child as Drone

      // Remove drones that are too far behind and still active
      if (drone.active && drone.x < scrollX - cleanupDistance) {
        console.log(`Cleaning up drone at x=${drone.x} (behind camera)`)

        // Clear zone occupation
        const zone = this.getZoneForPosition(drone.x)
        if (this.occupiedZones.get(zone) === 'drone') {
          this.occupiedZones.delete(zone)
        }

        // Disable physics body
        const droneBody = drone.body as Phaser.Physics.Arcade.StaticBody
        if (droneBody) {
          droneBody.enable = false
        }

        // Remove from group
        this.drones.remove(drone, true, true)
      }

      return true
    })

    // Continuously spawn new drones ahead
    const spawnZone = this.getZoneForPosition(rightEdge + 400)

    // Only spawn if this zone is not occupied
    if (!this.occupiedZones.has(spawnZone)) {
      const zoneStart = spawnZone * this.ZONE_WIDTH
      const newX = zoneStart + Phaser.Math.Between(100, this.ZONE_WIDTH - 100)
      this.spawnDrone(newX)
    }
  }

  private wrapBookcases() {
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    let width = this.bookcase1.width * 2
    if (this.bookcase1.x + width < scrollX) {
      this.bookcase1.x = Phaser.Math.Between(rightEdge + width, rightEdge + width + 800)
      const overlap = this.windows.find((win) => {
        return Math.abs(this.bookcase1.x - win.x) <= win.width
      })

      this.bookcase1.visible = !overlap
    }

    width = this.bookcase2.width
    if (this.bookcase2.x + width < scrollX) {
      this.bookcase2.x = Phaser.Math.Between(this.bookcase1.x + width, this.bookcase1.x + width + 800)
      const overlap = this.windows.find((win) => {
        return Math.abs(this.bookcase2.x - win.x) <= win.width
      })

      this.bookcase2.visible = !overlap

      // this.spawnCoins()
    }
  }

  private wrapWindows() {
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    let width = this.window1.width * 2
    if (this.window1.x + width < scrollX) {
      this.window1.x = Phaser.Math.Between(rightEdge + width, rightEdge + width + 800)
      const overlap = this.bookcases.find((bc) => {
        return Math.abs(this.window1.x - bc.x) <= this.window1.width
      })

      this.window1.visible = !overlap
    }

    width = this.window2.width
    if (this.window2.x + width < scrollX) {
      this.window2.x = Phaser.Math.Between(this.window1.x + width, this.window1.x + width + 800)
      const overlap = this.bookcases.find((bc) => {
        return Math.abs(this.window2.x - bc.x) <= this.window2.width
      })

      this.window2.visible = !overlap
    }
  }

  private wrapMouseHole() {
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    if (this.mouseHole.x + this.mouseHole.width < scrollX) {
      this.mouseHole.x = Phaser.Math.Between(rightEdge + 100, rightEdge + 1000)
    }
  }

  private spawnFlag() {
    const height = this.scale.height
    const scrollX = this.cameras.main.scrollX
    const rightEdge = scrollX + this.scale.width

    // Don't spawn flags in the BRIT CARD area (X: 700-2600)
    if (rightEdge < 2600) {
      return
    }

    // Find available zone for flag (must be different from laser zones)
    const startZone = this.getZoneForPosition(rightEdge)
    const availableZone = this.findAvailableZone(startZone)

    // Place flag in the available zone (random position within zone)
    const zoneStart = availableZone * this.ZONE_WIDTH
    const newX = zoneStart + Phaser.Math.Between(1000, this.ZONE_WIDTH - 1000)

    // Mark zone as occupied
    this.occupiedZones.set(availableZone, 'flag')

    // Randomly select a flag texture from available flags
    const randomFlagTexture = Phaser.Utils.Array.GetRandom(this.availableFlagTextures)

    // Randomize vertical position within the ground area
    // Ground area is approximately from Y=520 to Y=660 (bottom ~140 pixels)
    const minY = height - 200 // Top of ground area
    const maxY = height - 60 // Bottom of ground area (original position)
    const randomY = Phaser.Math.Between(minY, maxY)

    // Create new flag with randomly selected texture and random vertical position
    const flag = this.flags.create(newX, randomY, randomFlagTexture).setOrigin(0.5, 1).setScale(1, 0.5) as Phaser.Physics.Arcade.Sprite
    const flagBody = flag.body as Phaser.Physics.Arcade.StaticBody
    flagBody.enable = true
    flagBody.updateFromGameObject()
  }

  private wrapFlags() {
    // Check if it's time to spawn a new flag
    const currentTime = this.time.now
    if (currentTime - this.lastFlagSpawnTime >= this.flagSpawnInterval) {
      this.spawnFlag()
      this.lastFlagSpawnTime = currentTime
    }

    // Remove flags that have gone off screen
    const scrollX = this.cameras.main.scrollX
    this.flags.children.each((child) => {
      const flag = child as Phaser.Physics.Arcade.Sprite
      if (flag.x + flag.width < scrollX) {
        // Clear zone occupation
        const zone = this.getZoneForPosition(flag.x)
        if (this.occupiedZones.get(zone) === 'flag') {
          this.occupiedZones.delete(zone)
        }

        this.flags.killAndHide(flag)
        if (flag.body) {
          ;(flag.body as Phaser.Physics.Arcade.StaticBody).enable = false
        }
      }
      return true
    })
  }

  private createTouchControls(width: number, height: number) {
    const buttonWidth = 240 // Increased by 200% (was 80)
    const buttonHeight = 120 // Decreased by 50% (was 240)
    const buttonMargin = 20

    // Create throw flag button (right side, bottom)
    this.throwFlagButton = this.add.graphics()
    this.throwFlagButton.fillStyle(0xe24a4a, 0.7)
    this.throwFlagButton.fillRoundedRect(0, 0, buttonWidth, buttonHeight, 10)
    this.throwFlagButton.setPosition(width - buttonWidth - buttonMargin, height - buttonHeight - buttonMargin)
    this.throwFlagButton.setScrollFactor(0)
    this.throwFlagButton.setInteractive(new Phaser.Geom.Rectangle(0, 0, buttonWidth, buttonHeight), Phaser.Geom.Rectangle.Contains)
    this.throwFlagButton.setDepth(1000)

    // Add flag icon to throw button
    this.throwFlagButtonIcon = this.add.text(width - buttonWidth / 2 - buttonMargin, height - buttonHeight / 2 - buttonMargin, '🇬🇧', {
      fontSize: '60px', // Scaled proportionally
    })
    this.throwFlagButtonIcon.setOrigin(0.5)
    this.throwFlagButtonIcon.setScrollFactor(0)
    this.throwFlagButtonIcon.setDepth(1001)

    // Throw flag button handler - tap to reload when out of flags or throw flag
    this.throwFlagButton.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.launchBritishFlag()
      this.throwFlagButton?.setAlpha(1)
      this.time.delayedCall(200, () => {
        this.throwFlagButton?.setAlpha(0.7)
      })
      // Stop propagation to prevent jetpack activation
      pointer.event.stopPropagation()
    })

    // Handle screen tap for jetpack - tap anywhere except flag button
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Get flag button bounds
      const flagButtonBounds = new Phaser.Geom.Rectangle(
        width - buttonWidth - buttonMargin,
        height - buttonHeight - buttonMargin,
        buttonWidth,
        buttonHeight
      )

      // Check if pointer is NOT in the flag button area
      if (!flagButtonBounds.contains(pointer.x, pointer.y)) {
        console.log('🚀 Screen tapped - activating jetpack!')
        this.isJetpackPressed = true
      }
    })

    this.input.on('pointerup', () => {
      console.log('🚀 Screen released - deactivating jetpack!')
      this.isJetpackPressed = false
    })
  }

  private isThrowFlagPressed(): boolean {
    // This is now handled by the button tap in createMobileControls
    return false
  }

  private startReload() {
    console.log('🔄 Starting flag reload...')
    this.isReloading = true
    this.reloadStartTime = this.time.now

    const { width, height } = this.scale
    const centerX = width * 0.5
    const centerY = height * 0.5

    // Create reload text
    this.reloadText = this.add
      .text(centerX, centerY - 50, 'RELOADING FLAGS...', {
        fontSize: '32px',
        color: '#FFD700',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(2000)

    // Create reload bar background
    this.reloadBar = this.add.graphics()
    this.reloadBar.setScrollFactor(0)
    this.reloadBar.setDepth(2000)

    // Draw background
    this.reloadBar.fillStyle(0x333333, 0.8)
    this.reloadBar.fillRect(centerX - 200, centerY, 400, 30)
  }

  private updateReload(currentTime: number) {
    const elapsed = currentTime - this.reloadStartTime
    const progress = Math.min(elapsed / this.RELOAD_DURATION, 1)

    // Update reload bar
    if (this.reloadBar) {
      const { width, height } = this.scale
      const centerX = width * 0.5
      const centerY = height * 0.5

      this.reloadBar.clear()

      // Draw background
      this.reloadBar.fillStyle(0x333333, 0.8)
      this.reloadBar.fillRect(centerX - 200, centerY, 400, 30)

      // Draw progress
      this.reloadBar.fillStyle(0x00ff00, 1)
      this.reloadBar.fillRect(centerX - 200, centerY, 400 * progress, 30)

      // Draw border
      this.reloadBar.lineStyle(3, 0xffffff, 1)
      this.reloadBar.strokeRect(centerX - 200, centerY, 400, 30)
    }

    // Check if reload is complete
    if (progress >= 1) {
      this.completeReload()
    }
  }

  private completeReload() {
    console.log('✅ Reload complete!')
    this.isReloading = false
    this.flagsRemaining = this.MAX_FLAGS
    this.updateFlagCountDisplay()

    // Clean up reload UI
    if (this.reloadBar) {
      this.reloadBar.destroy()
      this.reloadBar = undefined
    }
    if (this.reloadText) {
      // Show success message briefly
      this.reloadText.setText('RELOAD COMPLETE!')
      this.reloadText.setColor('#00FF00')

      this.time.delayedCall(1000, () => {
        this.reloadText?.destroy()
        this.reloadText = undefined
      })
    }
  }

  private cancelReload() {
    console.log('❌ Reload cancelled!')
    this.isReloading = false

    // Clean up reload UI
    if (this.reloadBar) {
      this.reloadBar.destroy()
      this.reloadBar = undefined
    }
    if (this.reloadText) {
      this.reloadText.setText('RELOAD CANCELLED!')
      this.reloadText.setColor('#FF0000')

      this.time.delayedCall(1000, () => {
        this.reloadText?.destroy()
        this.reloadText = undefined
      })
    }
  }

  private handlePause() {
    console.log('🔴 Game paused (tab hidden)')
    // Record when the game was paused
    this.pauseStartTime = this.time.now
  }

  private handleResume() {
    console.log('🟢 Game resumed (tab visible)')
    // Calculate how long the game was paused and add it to the total paused time
    if (this.pauseStartTime > 0) {
      const pauseDuration = this.time.now - this.pauseStartTime
      this.pausedTime += pauseDuration
      console.log(`⏱️ Paused for ${pauseDuration}ms, total paused time: ${this.pausedTime}ms`)
      this.pauseStartTime = 0
    }
  }
}
